import { useState, useEffect, useCallback } from 'react'
import { getCurrencySymbol } from '../../utils/currencyUtils'
import { Box, Typography, CircularProgress, IconButton } from '@mui/material'
import { Edit as EditIcon } from '@mui/icons-material'
import { supabase } from '../../utils/supabase'
import posthog, { isPostHogConfigured } from '../../posthog'
import { logPostHogWorkflow } from '../../posthogLogger'
import {
	convertToUtcTimestamps,
	applyDurationChangesToAppointments,
	getAppointmentDuration,
	SLOT_INTERVAL_MINUTES
} from '../../utils/appointmentUtils'
import { getFullName } from '../../utils/clientUtils'
import DropdownCalendar from '../ui/DropdownCalendar'
import TimeListPicker from '../ui/TimeListPicker'
import OverlayScrollThumb from '../ui/OverlayScrollThumb'
import {
	L,
	Icon,
	LavSectionTitle,
	RepeatControl,
	REPEAT_DEFAULT,
} from './appointmentLavenderFields'
import PetAppointmentCard from './PetAppointmentCard'
import { fmtClock as slotClock, fmtFull as slotFull, timeToMins as slotMins } from '../appointment-details/rescheduleShared'
import DurationChangePopUp from '../DurationChangePopUp'
import { v4 as uuidv4 } from 'uuid'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * AppointmentsOverview - Review and book/save grouped appointments (Step 2)
 *
 * @param {object} merchant - Merchant object
 * @param {object} client - Selected client
 * @param {string} date - Selected date (YYYY-MM-DD)
 * @param {string} time - Selected time (HH:mm)
 * @param {array} appointments - Array of appointment objects (with optional 'id' for edit mode)
 * @param {array} removedAppointmentIds - Edit mode: IDs of existing appointments the user removed;
 *   they are deleted only when Save succeeds
 * @param {function} onTimeChange - Callback when time changes
 * @param {function} onDateChange - Callback when date changes
 * @param {function} onAddPet - Callback to add another pet
 * @param {function} onEditAppointment - Callback to edit an appointment (index)
 * @param {function} onDeleteAppointment - Callback to delete an appointment (index)
 * @param {function} onBook - Callback when booking/saving is confirmed
 * @param {function} onCancel - Callback to cancel
 * @param {function} onSetupRecurring - When provided (single-pet, non-edit), turning on the
 *   inline recurring control routes Book to the recurring series flow with a `repeat` config.
 * @param {boolean} isMobile - Mobile view flag
 * @param {boolean} isEditMode - Whether we're editing existing appointments
 * @param {string} groupId - Group ID for existing grouped appointments (edit mode)
 * @param {function} onAppointmentsChange - Callback when appointments are modified (e.g., duration change)
 */
export default function AppointmentsOverview({
	merchant,
	client,
	date,
	time,
	appointments = [],
	removedAppointmentIds = [],
	onTimeChange,
	onDateChange,
	onAddPet,
	onEditAppointment,
	onDeleteAppointment,
	onBook,
	onCancel,
	onSetupRecurring = null,
	isMobile = false,
	saveActionRef = null,
	isEditMode = false,
	groupId = null,
	onAppointmentsChange = null,
	// Edit flow (design "Edit appointment" / AprSlotField): when the host
	// supplies onPickSlot, the When section is a single "Change" field that
	// opens the host's slot picker instead of the inline date + time pickers —
	// moving a visit always goes through the reschedule picker.
	onPickSlot = null,
	// Edit flow: called with { date, time } before saving; returning true means
	// the host took over (it shows the move-confirm step and then calls the
	// registered save action with { skipMoveConfirm: true }).
	onInterceptSave = null,
}) {
	// Inject Outfit font (lavender design typeface)
	useEffect(() => {
		const id = 'outfit-font-link'
		if (!document.getElementById(id)) {
			const link = document.createElement('link')
			link.id = id
			link.rel = 'stylesheet'
			link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap'
			document.head.appendChild(link)
		}
	}, [])

	const [notes, setNotes] = useState('')
	const [booking, setBooking] = useState(false)
	const [error, setError] = useState('')
	const [employees, setEmployees] = useState([])
	const [availabilityWarning, setAvailabilityWarning] = useState('')
	const [durationPopupAnchor, setDurationPopupAnchor] = useState(null)

	// Inline recurring schedule. Set up here (not on the individual appointment
	// form) so the series covers the whole visit — every pet, each with its own
	// service, add-ons and duration.
	const [repeat, setRepeat] = useState(REPEAT_DEFAULT)
	const recurringAvailable = Boolean(onSetupRecurring) && !isEditMode && appointments.length > 0
	const isRecurring = recurringAvailable && repeat.on

	// Initialize notes from first appointment in edit mode
	useEffect(() => {
		if (isEditMode && appointments.length > 0 && appointments[0].comments) {
			setNotes(appointments[0].comments)
		}
	}, [isEditMode, appointments])

	// Calculate totals
	const totalPrice = appointments.reduce((sum, apt) => {
		const servicePrice = apt.service?.price || 0
		const addOnsPrice = (apt.addOns || []).reduce((s, a) => s + (a.price || 0), 0)
		return sum + servicePrice + addOnsPrice
	}, 0)

	// Use getAppointmentDuration to correctly calculate duration from timestamps (for edit mode)
	// or from service + addOns (for add mode)
	const totalDuration = appointments.reduce((sum, apt) => {
		return sum + getAppointmentDuration(apt)
	}, 0)

	const cs = getCurrencySymbol(merchant?.currency)
	const formatCurrency = (cents) => `${cs}${(cents / 100).toFixed(2)}`

	// Load employees for availability check
	const loadEmployees = useCallback(async () => {
		if (!merchant?.id) return

		try {
			const { data, error } = await supabase
				.from('employees')
				.select('id, first_name, last_name, services')
				.eq('merchant_id', merchant.id)
				.eq('active', true)

			if (error) throw error
			setEmployees(data || [])
		} catch (e) {
			console.error('Error loading employees:', e)
		}
	}, [merchant?.id])

	useEffect(() => {
		loadEmployees()
	}, [loadEmployees])

	// Check for overlapping appointments (warning only, does not block)
	useEffect(() => {
		const checkOverlap = async () => {
			if (!time || !date || appointments.length === 0 || !merchant) {
				setAvailabilityWarning('')
				return
			}

			try {
				// Calculate UTC timestamps for the entire booking
				const { utcStart, utcEnd } = convertToUtcTimestamps(
					date,
					time,
					merchant.timezone,
					totalDuration
				)

				// Get IDs of appointments being edited (to exclude from overlap check)
				const editingAppointmentIds = isEditMode
					? [...appointments.filter(apt => apt.id).map(apt => apt.id), ...removedAppointmentIds]
					: []

				// Fetch existing appointments
				const { data: existingAppointments } = await supabase
					.from('appointments')
					.select('id, service_id, employee_id, start_ts, end_ts, customer_name')
					.eq('merchant_id', merchant.id)
					.in('status', ['booked', 'bookPending'])
					.lt('start_ts', utcEnd)
					.gt('end_ts', utcStart)

				// Check if any assigned employees have overlapping appointments
				const assignedEmployeeIds = appointments
					.map(apt => apt.employee?.id)
					.filter(Boolean)

				// Filter out the appointments being edited to avoid double counting
				const overlappingByEmployee = (existingAppointments || [])
					.filter(apt => !editingAppointmentIds.includes(apt.id)) // Exclude appointments being edited
					.filter(apt => apt.employee_id && assignedEmployeeIds.includes(apt.employee_id))

				if (overlappingByEmployee.length > 0) {
					// Build warning message
					const warningParts = []
					assignedEmployeeIds.forEach(empId => {
						const empOverlaps = overlappingByEmployee.filter(apt => apt.employee_id === empId)
						if (empOverlaps.length > 0) {
							const emp = employees.find(e => e.id === empId)
							const empName = emp ? `${emp.first_name} ${emp.last_name}` : 'Employee'
							const details = empOverlaps.map(apt => {
								const startTime = dayjs(apt.start_ts).tz(merchant.timezone).format('h:mm A')
								const endTime = dayjs(apt.end_ts).tz(merchant.timezone).format('h:mm A')
								return `${apt.customer_name || 'Unknown'} (${startTime} - ${endTime})`
							}).join(', ')
							warningParts.push(`${empName} has ${empOverlaps.length} overlapping appointment${empOverlaps.length > 1 ? 's' : ''}: ${details}`)
						}
					})
					setAvailabilityWarning(warningParts.join('. '))
				} else {
					setAvailabilityWarning('')
				}
			} catch (e) {
				console.error('Error checking overlap:', e)
			}
		}

		checkOverlap()
	}, [time, date, appointments, merchant, totalDuration, employees, isEditMode, removedAppointmentIds])

	// Calculate end time display
	const getEndTime = () => {
		if (!time) return null
		const startTime = dayjs(`2000-01-01 ${time}`)
		return startTime.add(totalDuration, 'minute').format('h:mm A')
	}

	// Handle duration popup open
	const handleDurationEditClick = (event) => {
		setDurationPopupAnchor(event.currentTarget)
	}

	// Handle duration popup close
	const handleDurationPopupClose = () => {
		setDurationPopupAnchor(null)
	}

	// Handle duration changes from the popup
	const handleDurationChange = (newDurations) => {
		if (!onAppointmentsChange || !merchant) return

		// Apply the new durations to appointments
		const updatedAppointments = applyDurationChangesToAppointments(
			appointments,
			newDurations,
			time,
			date,
			merchant.timezone
		)

		// Notify parent of the changes
		onAppointmentsChange(updatedAppointments)
	}

	// Handle book/save
	// Note: Overlapping appointments are allowed for merchants (warning is informational only)
	const handleBook = async (opts = null) => {
		if (!client || !date || !time || appointments.length === 0) {
			setError('Please complete all required fields')
			return
		}

		// Edit flow: a moved start time goes through the reschedule confirm step
		// (old → new, notify the client) before anything is written. The host
		// re-enters here with { skipMoveConfirm: true } once confirmed. The
		// button's click event is not an opts object, so it never skips.
		if (isEditMode && onInterceptSave && opts?.skipMoveConfirm !== true) {
			if (onInterceptSave({ date, time })) return
		}

		// Recurring: hand the whole visit to the series flow. Every pet travels
		// with its own service / add-ons / duration, so each occurrence rebuilds
		// the same multi-pet booking.
		if (isRecurring) {
			onSetupRecurring({
				appointments,
				client,
				date,
				time,
				notes,
				repeat,
				totalDuration,
			})
			return
		}

		setBooking(true)
		setError('')

		try {
			if (isEditMode) {
				// Edit mode: Update existing appointments
				let currentStartTime = dayjs.tz(
					`${date} ${time}`,
					merchant.timezone
				)

				// One group id for the whole visit. Reuse the existing group's id;
				// a single appointment that gains a pet gets a fresh id shared by
				// every row (generating it per row would split the visit).
				const editGroupId = appointments.length > 1 ? (groupId || uuidv4()) : null

				// Update or create appointments
				for (let i = 0; i < appointments.length; i++) {
					const apt = appointments[i]
					const service = apt.service
					const addOns = apt.addOns || []

					// Calculate duration - use getAppointmentDuration to handle both
					// modified durations (from timestamps) and default service durations
					const duration = getAppointmentDuration(apt)

					// Build add-ons data
					const addOnsData = addOns.map(addon => ({
						service_id: addon.id,
						name: addon.name,
						price: addon.price,
						duration_minutes: addon.duration_minutes || 0
					}))

					const aptUtcStart = currentStartTime.utc().toISOString()
					const aptUtcEnd = currentStartTime.add(duration, 'minute').utc().toISOString()

					const appointmentData = {
						merchant_id: merchant.id,
						service_id: service?.id,
						employee_id: apt.employee?.id || null,
						user_id: client.id,
						pet_id: apt.pet?.id,
						start_ts: aptUtcStart,
						end_ts: aptUtcEnd,
						customer_name: getFullName(client),
						customer_email: client.email || null,
						phone_number: client.phone_number || null,
						add_ons: addOnsData.length > 0 ? addOnsData : null,
						group_id: editGroupId,
						is_group_primary: i === 0,
						comments: notes.trim() || null
					}

					if (apt.id) {
						// Update existing appointment
						const { error: updateError } = await supabase
							.from('appointments')
							.update(appointmentData)
							.eq('id', apt.id)

						if (updateError) throw updateError
					} else {
						// Create new appointment (added pet in edit mode)
						appointmentData.status = 'booked'
						const { error: insertError } = await supabase
							.from('appointments')
							.insert(appointmentData)

						if (insertError) throw insertError
					}

					// Next appointment starts after this one
					currentStartTime = currentStartTime.add(duration, 'minute')
				}

				// Delete the pets removed in this edit session (deferred until Save so
				// cancelling the edit never touches the booking).
				const idsToDelete = removedAppointmentIds.filter(id => id && !appointments.some(apt => apt.id === id))
				if (idsToDelete.length > 0) {
					const { error: deleteError } = await supabase
						.from('appointments')
						.delete()
						.in('id', idsToDelete)

					if (deleteError) throw deleteError
				}

			} else {
				// Create mode: Insert new appointments
				// Generate group_id if multiple appointments
				const newGroupId = appointments.length > 1 ? uuidv4() : null

				// Create appointments
				const appointmentsToCreate = []
				let currentStartTime = dayjs.tz(
					`${date} ${time}`,
					merchant.timezone
				)

				for (let i = 0; i < appointments.length; i++) {
					const apt = appointments[i]
					const service = apt.service
					const addOns = apt.addOns || []

					// Calculate duration - use getAppointmentDuration to handle both
					// modified durations (from timestamps) and default service durations
					const duration = getAppointmentDuration(apt)

					// Build add-ons data
					const addOnsData = addOns.map(addon => ({
						service_id: addon.id,
						name: addon.name,
						price: addon.price,
						duration_minutes: addon.duration_minutes || 0
					}))

					const aptUtcStart = currentStartTime.utc().toISOString()
					const aptUtcEnd = currentStartTime.add(duration, 'minute').utc().toISOString()

					const appointmentData = {
						merchant_id: merchant.id,
						service_id: service?.id,
						employee_id: apt.employee?.id || null,
						user_id: client.id,
						pet_id: apt.pet?.id,
						start_ts: aptUtcStart,
						end_ts: aptUtcEnd,
						status: 'booked',
						customer_name: getFullName(client),
						customer_email: client.email || null,
						phone_number: client.phone_number || null,
						add_ons: addOnsData.length > 0 ? addOnsData : null,
						group_id: newGroupId,
						is_group_primary: i === 0,
						comments: notes.trim() || null
					}

					appointmentsToCreate.push(appointmentData)

					// Next appointment starts after this one
					currentStartTime = currentStartTime.add(duration, 'minute')
				}

				// Insert all appointments
				const { data: insertedAppts, error: insertError } = await supabase
					.from('appointments')
					.insert(appointmentsToCreate)
					.select('id, is_group_primary')

				if (insertError) throw insertError

				// Send agreement emails to client (fire-and-forget)
				const primaryApptId = insertedAppts?.find(a => a.is_group_primary)?.id
					?? insertedAppts?.[0]?.id ?? null
				if (client?.id && client?.email) {
					supabase.functions.invoke('send-agreement', {
						body: {
							clientId: client.id,
							merchantId: merchant.id,
							channel: 'email',
							...(primaryApptId ? { appointmentId: primaryApptId } : {}),
						}
					}).catch(err => console.error('Failed to send agreement emails:', err))
				}
			}

			// Success
			if (!isEditMode && isPostHogConfigured) {
				const appointmentLogAttributes = {
					event: 'appointment_created',
					appointment_count: appointments.length,
					is_group_booking: appointments.length > 1,
					total_amount: totalPrice,
					total_duration_minutes: totalDuration,
				}
				posthog.capture('appointment_created', {
					appointment_count: appointments.length,
					is_group_booking: appointments.length > 1,
					total_amount: totalPrice,
					total_duration_minutes: totalDuration,
				})
				logPostHogWorkflow('appointment created', appointmentLogAttributes)
			}
			// Awaited so an async host (the edit flow notifying the client of a
			// move) keeps the Saving… state until it is done.
			await onBook()

		} catch (e) {
			console.error(isEditMode ? 'Save error:' : 'Booking error:', e)
			setError(e.message || `Failed to ${isEditMode ? 'save changes' : 'create booking'}. Please try again.`)
		} finally {
			setBooking(false)
		}
	}

	// Register book action for mobile header button
	useEffect(() => {
		if (saveActionRef) {
			saveActionRef.current = handleBook
		}
		return () => {
			if (saveActionRef) {
				saveActionRef.current = null
			}
		}
	})

	const bookLabel = isRecurring ? 'Set up recurring' : (isEditMode ? 'Save' : 'Book')

	return (
		<Box sx={{
			display: 'flex',
			flexDirection: 'column',
			height: '100%',
			overflow: 'hidden',
			fontFamily: L.font,
		}}>
			{/* Scrollable Content */}
			<Box className="lav-overlay-scroll" sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '18px',
				flex: 1,
				overflow: 'auto',
				overflowX: 'hidden',
				p: '4px 16px 0',
			}}>
				<OverlayScrollThumb />
				{error && (
					<Box sx={{ ...alertSx, bgcolor: '#FFF0F0', border: '1px solid #FECACA', color: '#8B1A1A', cursor: 'pointer' }} onClick={() => setError('')}>
						<Box sx={{ flex: 'none', display: 'flex', color: '#EF4444' }}><Icon name="warn" size={15} /></Box>
						<Box sx={{ flex: 1 }}>{error}</Box>
						<Box sx={{ flex: 'none', display: 'flex' }}><Icon name="x" size={13} /></Box>
					</Box>
				)}

				{availabilityWarning && (
					<Box sx={{ ...alertSx, bgcolor: '#FDF3E4', border: '1.5px solid #F2DDB4', color: '#8F6217' }}>
						<Box sx={{ flex: 'none', display: 'flex', color: '#C08A2D', mt: '1px' }}><Icon name="warn" size={15} /></Box>
						<Box sx={{ flex: 1 }}>{availabilityWarning}</Box>
					</Box>
				)}

				{/* When Section */}
				<Box component="section">
					<LavSectionTitle>When</LavSectionTitle>
					{isEditMode && onPickSlot ? (
						// Design AprSlotField: one trigger showing the visit's slot; tapping
						// it opens the host's slot picker ("Change date & time"). The time
						// is never typed in directly here.
						<Box
							component="button"
							type="button"
							aria-haspopup="dialog"
							onClick={onPickSlot}
							sx={{
								display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
								height: isMobile ? '50px' : '42px', px: isMobile ? '13px' : '12px',
								border: `1.5px solid ${L.border}`, borderRadius: isMobile ? '12px' : '10px',
								bgcolor: L.field, fontFamily: L.font, fontSize: isMobile ? '15px' : '14px',
								fontWeight: 600, color: L.text, cursor: 'pointer', textAlign: 'left',
								transition: 'border-color .15s, box-shadow .15s',
								'&:hover': { borderColor: L.primary },
								'&:hover .ap-slot-go': { color: L.primary },
								'&:focus-visible': { outline: 'none', borderColor: L.primary, boxShadow: `0 0 0 3px ${L.primarySoft}` },
								'& > svg': { color: L.muted, flex: 'none' },
							}}
						>
							<Icon name="calendar" size={15} />
							<Box component="span" sx={{
								flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
								color: date && time ? L.text : L.muted, fontWeight: date && time ? 600 : 500,
							}}>
								{date && time
									? `${slotFull(date)} · ${slotClock(slotMins(time))} – ${slotClock(slotMins(time) + totalDuration)}`
									: 'Pick a date & time'}
							</Box>
							<Box component="span" sx={{ flex: 'none', fontSize: '12px', fontWeight: 700, color: L.primaryDeep }}>
								{date && time ? 'Change' : 'Pick'}
							</Box>
							<Box className="ap-slot-go" component="span" sx={{ display: 'flex', color: L.muted, transform: 'rotate(-90deg)' }}>
								<Icon name="chevron" size={14} />
							</Box>
						</Box>
					) : (
						<Box sx={{ display: 'grid', gridTemplateColumns: '1.15fr 1fr', gap: '10px' }}>
							<DropdownCalendar
								value={date}
								onChange={onDateChange}
								min={dayjs().format('YYYY-MM-DD')}
								placeholder="Pick a date"
							/>
							<TimeListPicker
								value={time}
								onChange={onTimeChange}
								step={SLOT_INTERVAL_MINUTES}
								align="right"
							/>
						</Box>
					)}
					{time && (
						<Typography sx={{ fontSize: isMobile ? '13px' : '12.5px', color: L.muted, mt: '7px', fontWeight: 600, fontFamily: L.font }}>
							Ends at {getEndTime()}
						</Typography>
					)}

					{/* Recurring schedule — per the design's step 2, the repeat control
					    lives inside When (after "Ends at", separated by .ap-gap) and is
					    always visible: the switch itself is the on/off affordance. */}
					{recurringAvailable && (
						<>
							<Box sx={{ height: '10px' }} />
							<RepeatControl
								repeat={repeat}
								setRepeat={setRepeat}
								dateStr={date}
								EndDatePicker={({ value, onChange, min, max, placeholder }) => (
									<DropdownCalendar
										value={value}
										onChange={onChange}
										min={min}
										max={max}
										placeholder={placeholder}
									/>
								)}
							/>
						</>
					)}
				</Box>

				{/* Client Details Section */}
				<Box component="section">
					<LavSectionTitle>Client details</LavSectionTitle>
					<Box sx={{
						display: 'flex', alignItems: 'center', gap: '9px',
						border: `1.5px solid ${L.border}`, borderRadius: isMobile ? '12px' : L.rSm,
						bgcolor: L.surface, height: isMobile ? '48px' : '42px', px: isMobile ? '13px' : '12px',
					}}>
						<Box sx={{ display: 'flex', color: L.muted, flex: 'none' }}><Icon name="user" size={15} /></Box>
						<Box component="span" sx={{ fontSize: isMobile ? '15px' : '13.5px', fontWeight: 600, color: L.text, fontFamily: L.font }}>
							{getFullName(client)}
						</Box>
					</Box>
				</Box>

				{/* Pet Appointments Cards */}
				<Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
					{appointments.map((apt, index) => (
						<PetAppointmentCard
							key={apt.pet?.id || index}
							appointment={apt}
							onEdit={() => onEditAppointment(index)}
							onDelete={() => onDeleteAppointment(index)}
							showEdit={true}
							showDelete={isEditMode || appointments.length > 1}
							currencySymbol={cs}
						/>
					))}

					{/* Add Another Pet Button */}
					<Box
						component="button"
						type="button"
						onClick={onAddPet}
						sx={{
							display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
							width: '100%', height: isMobile ? '50px' : '42px',
							borderRadius: isMobile ? '12px' : L.rSm, cursor: 'pointer',
							border: `1.5px dashed ${L.border}`, bgcolor: 'transparent',
							color: L.primaryDeep, fontWeight: 700, fontSize: isMobile ? '14.5px' : '13.5px', fontFamily: L.font,
							'&:hover': { borderColor: L.primary, bgcolor: L.primarySoft },
						}}
					>
						<Icon name="plus" size={14} />
						<span>{appointments.length === 0 ? 'Add a pet' : 'Add another pet'}</span>
					</Box>
					{isEditMode && appointments.length === 0 && (
						<Typography sx={{ fontSize: isMobile ? '13px' : '12.5px', color: L.muted, fontWeight: 600, fontFamily: L.font, textAlign: 'center' }}>
							Add at least one pet to save changes.
						</Typography>
					)}
				</Box>

				{/* Appointment Notes */}
				<Box component="section">
					<LavSectionTitle hint="optional">Appointment notes</LavSectionTitle>
					<Box
						component="textarea"
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						placeholder="Add notes"
						rows={2}
						sx={{
							width: '100%', border: `1.5px solid ${L.border}`, borderRadius: isMobile ? '12px' : L.rSm,
							p: isMobile ? '12px 13px' : '10px 12px', fontFamily: L.font,
							fontSize: isMobile ? '15px' : '13.5px', resize: 'vertical',
							bgcolor: L.field, minHeight: isMobile ? '72px' : '56px', color: L.text, outline: 'none', boxSizing: 'border-box',
							'&::placeholder': { color: L.muted },
							'&:focus': { borderColor: L.primary, boxShadow: `0 0 0 3px ${L.primarySoft}` },
						}}
					/>
				</Box>
			</Box>

			{/* Bottom Fixed Section */}
			<Box sx={{
				display: 'flex', flexDirection: 'column', gap: '12px',
				pt: '13px', pb: isMobile ? '0px' : '20px', px: isMobile ? 0 : '16px',
				mt: 'auto', bgcolor: L.bg,
			}}>
				{/* Total */}
				<Box sx={{
					display: 'flex', alignItems: 'center', gap: '8px',
					borderTop: `1.5px solid ${L.border}`, pt: '13px',
					px: isMobile ? '16px' : '0', fontSize: isMobile ? '15px' : '14px', fontFamily: L.font,
				}}>
					<Box component="span" sx={{ color: L.muted, fontWeight: 600 }}>Total:</Box>
					<Box component="strong" sx={{
						ml: 'auto', fontSize: isMobile ? '17px' : '15.5px', fontWeight: 800, color: L.text,
						fontVariantNumeric: 'tabular-nums',
					}}>
						{formatCurrency(totalPrice)} ({totalDuration} mins)
					</Box>
					{onAppointmentsChange && appointments.length > 0 && (
						<IconButton
							onClick={handleDurationEditClick}
							size="small"
							sx={{ p: '4px', color: L.muted, borderRadius: '8px', '&:hover': { bgcolor: L.surface, color: L.text } }}
						>
							<EditIcon sx={{ fontSize: 16 }} />
						</IconButton>
					)}
				</Box>

				{/* Duration Change Popup */}
				<DurationChangePopUp
					anchorEl={durationPopupAnchor}
					open={Boolean(durationPopupAnchor)}
					onClose={handleDurationPopupClose}
					appointments={appointments}
					onSave={handleDurationChange}
					isMobile={isMobile}
				/>

				{/* Action Buttons — desktop: Cancel + Book right-aligned; mobile: the
				    design's sticky full-width Book bar (Cancel lives in the header X). */}
				<Box sx={{
					display: 'flex', gap: '10px', justifyContent: 'flex-end',
					px: isMobile ? '16px' : 0,
					pb: isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 20px)' : 0,
				}}>
					{!isMobile && (
						<Box
							component="button"
							type="button"
							onClick={onCancel}
							disabled={booking}
							sx={{
								px: '16px', height: '42px', borderRadius: L.rSm,
								border: `1.5px solid ${L.border}`, bgcolor: 'transparent',
								fontSize: '14px', fontWeight: 650, color: L.muted, fontFamily: L.font, cursor: 'pointer',
								'&:hover:not(:disabled)': { color: L.text, borderColor: L.muted },
								'&:disabled': { opacity: 0.5, cursor: 'default' },
							}}
						>
							Cancel
						</Box>
					)}
					<Box
						component="button"
						type="button"
						onClick={handleBook}
						disabled={booking || !time || appointments.length === 0}
						sx={{
							display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
							height: isMobile ? '52px' : '42px', px: '22px',
							borderRadius: isMobile ? '14px' : L.rSm, border: 'none',
							flex: isMobile ? 1 : 'none',
							bgcolor: L.primary, color: L.onPrimary,
							fontSize: isMobile ? '15.5px' : '14px', fontWeight: isMobile ? 750 : 700,
							fontFamily: L.font, whiteSpace: 'nowrap', cursor: 'pointer',
							boxShadow: `0 2px 8px ${L.primarySoft}`,
							'&:hover:not(:disabled)': { bgcolor: L.primaryDeep },
							'&:disabled': { opacity: 0.45, cursor: 'default', boxShadow: 'none' },
						}}
					>
						{booking ? <CircularProgress size={16} sx={{ color: L.onPrimary }} /> : (isRecurring ? <Icon name="repeat" size={15} /> : <Icon name="tick" size={15} />)}
						<span>{booking ? 'Saving…' : bookLabel}</span>
					</Box>
				</Box>
			</Box>
		</Box>
	)
}

const alertSx = {
	display: 'flex', alignItems: 'flex-start', gap: '8px', p: '11px 13px',
	borderRadius: '10px', fontSize: '13px', fontFamily: L.font,
}
